import React, { useEffect, useState } from "react";
import { Button } from "../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../design/dialog";
import { Input } from "../design/input";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  ErrorMessage,
} from "../design/fieldset";
import { Select } from "../design/select";
import { PlusIcon } from "@heroicons/react/20/solid";
import { useProfessional } from "../contexts/ProfessionalContext";
import { Professional } from "../models/Professional";
import { Checkbox, CheckboxField } from "../design/checkbox";
import { professionalRoleTypes } from "../models/ProfessionalRoleType";
import { Textarea } from "../design/textarea";
import { Subheading } from "../design/heading";
import { usaStates } from "typed-usa-states";
import SelectStatesField from "../components/Fields/SelectStatesField";

interface Props {
  professional?: Professional;
  className?: string;
  children?: React.ReactNode;
}

interface Errors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  bio?: string;
  roles?: string;
}

const ProfessionalButton: React.FC<Props> = ({
  professional,
  className,
  children,
}) => {
  const { createProfessional, updateProfessional } = useProfessional();
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [bio, setBio] = useState<string>("");
  const [roles, setRoles] = useState<string[]>([]);
  const [websiteUrl, setWebsiteUrl] = useState<string>("");
  const [schedulingUrl, setSchedulingUrl] = useState<string>("");
  const [hourlyRate, setHourlyRate] = useState<string>("");
  const [languagesSpoken, setLanguagesSpoken] = useState<string>("");

  const [city, setCity] = useState<string>("");
  const [state, setState] = useState<string>("");
  const [country, setCountry] = useState<string>("");
  const [licensedStates, setLicensedStates] = useState<string[]>([]);
  const [licensedCountries, setLicensedCountries] = useState<string[]>([]);
  const [offersVirtualRoles, setOffersVirtualRoles] = useState<boolean>(false);

  const [errors, setErrors] = useState<Errors>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (professional && isOpen) {
      setFirstName(professional.firstName ?? "");
      setLastName(professional.lastName ?? "");
      setEmail(professional.email ?? "");
      setPhoneNumber(professional.phoneNumber ?? "");
      setBio(professional.bio ?? "");
      setRoles(professional.roles ?? []);
      setWebsiteUrl(professional.websiteUrl ?? "");
      setSchedulingUrl(professional.schedulingUrl ?? "");
      setHourlyRate(professional.hourlyRate?.toString() ?? "");
      setLanguagesSpoken(professional.languagesSpoken?.join(", ") ?? "");
      setCity(professional.city ?? "");
      setState(professional.state ?? "");
      setCountry(professional.country ?? "");
      setLicensedStates(professional.licensedStates ?? []);
      setLicensedCountries(professional.licensedCountries ?? []);
      setOffersVirtualRoles(professional.offersVirtualRoles ?? false);
    }
  }, [professional, isOpen]);

  const validateForm = () => {
    const newErrors: Errors = {};
    if (!firstName.trim()) newErrors.firstName = "First name is required.";
    if (!lastName.trim()) newErrors.lastName = "Last name is required.";
    if (!bio.trim()) newErrors.bio = "Bio is required.";
    if (!roles.length)
      newErrors.roles = "At least one service type is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const clearForm = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhoneNumber("");
    setBio("");
    setRoles([]);
    setWebsiteUrl("");
    setSchedulingUrl("");
    setHourlyRate("");
    setLanguagesSpoken("");
    setCity("");
    setState("");
    setCountry("");
    setLicensedStates([]);
    setLicensedCountries([]);
    setOffersVirtualRoles(false);
    setErrors({});
    setIsOpen(false);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsSaving(true);
    try {
      if (professional) {
        await updateProfessional(professional._id, file, {
          firstName,
          lastName,
          email,
          phoneNumber,
          bio,
          roles,
          websiteUrl,
          schedulingUrl,
          hourlyRate: parseFloat(hourlyRate) || undefined,
          languagesSpoken: languagesSpoken.split(",").map((l) => l.trim()),
          city,
          state,
          country,
          licensedStates,
          licensedCountries,
          offersVirtualRoles,
        });
      } else {
        await createProfessional(file, {
          firstName,
          lastName,
          email,
          phoneNumber,
          bio,
          roles,
          websiteUrl: websiteUrl || undefined,
          schedulingUrl: schedulingUrl || undefined,
        });
      }
      clearForm();
    } catch (error) {
      alert("An error occurred. Please try again.");
      console.error(error);
    }
    setIsSaving(false);
  };

  return (
    <>
      <Button
        type="button"
        className={className}
        onClick={() => setIsOpen(true)}
      >
        {children ?? (
          <div className="flex items-center gap-2">
            <PlusIcon className="w-4 h-4" />
            <span className="hidden sm:block">
              {professional ? "Edit Professional" : "Add Professional"}
            </span>
          </div>
        )}
      </Button>
      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>
          {professional ? "Edit Professional" : "Add Professional"}
        </DialogTitle>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Image</Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
              </Field>
            </FieldGroup>
            <FieldGroup>
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
                <Field>
                  <Label>First Name</Label>
                  <Input
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First Name"
                  />
                  {errors.firstName && (
                    <ErrorMessage>{errors.firstName}</ErrorMessage>
                  )}
                </Field>
                <Field>
                  <Label>Last Name</Label>
                  <Input
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Last Name"
                  />
                  {errors.lastName && (
                    <ErrorMessage>{errors.lastName}</ErrorMessage>
                  )}
                </Field>
              </div>
            </FieldGroup>
            <FieldGroup>
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
                <Field>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email"
                  />
                </Field>
                <Field>
                  <Label>Phone Number</Label>
                  <Input
                    type="text"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="Phone Number"
                  />
                </Field>
              </div>
            </FieldGroup>
            <FieldGroup>
              <Field>
                <Label>Bio</Label>
                <Textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder="Bio"
                />
                {errors.bio && <ErrorMessage>{errors.bio}</ErrorMessage>}
              </Field>
              <Field>
                <Label>Roles</Label>
                {Object.entries(professionalRoleTypes).map(([key, value]) => (
                  <CheckboxField key={key}>
                    <Label>{value}</Label>
                    <Checkbox
                      checked={roles.includes(key)}
                      onChange={() => {
                        setRoles((prev) =>
                          prev.includes(key)
                            ? prev.filter((s) => s !== key)
                            : [...prev, key],
                        );
                      }}
                      value={key}
                      className="mr-2"
                    />
                  </CheckboxField>
                ))}
                {errors.roles && <ErrorMessage>{errors.roles}</ErrorMessage>}
              </Field>
              <Field>
                <Label>Website URL</Label>
                <Input
                  type="url"
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  placeholder="https:/example.com"
                />
              </Field>
              <Field>
                <Label>Scheduling URL</Label>
                <Input
                  type="url"
                  value={schedulingUrl}
                  onChange={(e) => setSchedulingUrl(e.target.value)}
                  placeholder="https:/calendly.com/yourlink"
                />
              </Field>
            </FieldGroup>

            <>
              <Subheading>Location</Subheading>
              <FieldGroup>
                <Field>
                  <Label>City</Label>
                  <Input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="City"
                  />
                </Field>
                <Field>
                  <Label>State</Label>
                  <Select
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                  >
                    <option value="">Select State</option>
                    {usaStates.map((s) => (
                      <option key={s.abbreviation} value={s.abbreviation}>
                        {s.name}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field>
                  <Label>Country</Label>
                  <Input
                    type="text"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    placeholder="Country"
                  />
                </Field>
                <SelectStatesField
                  title="Licensed States"
                  values={licensedStates}
                  onChange={setLicensedStates}
                />
                <Field>
                  <Label>Licensed Countries</Label>
                  <Input
                    type="text"
                    value={licensedCountries.join(", ")}
                    onChange={(e) =>
                      setLicensedCountries(
                        e.target.value.split(",").map((c) => c.trim()),
                      )
                    }
                    placeholder="Licensed Countries (comma separated)"
                  />
                </Field>
                <CheckboxField>
                  <Label>Offers Virtual Roles</Label>
                  <Checkbox
                    checked={offersVirtualRoles}
                    onChange={() => setOffersVirtualRoles(!offersVirtualRoles)}
                  />
                </CheckboxField>
              </FieldGroup>
            </>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={clearForm}>
            Cancel
          </Button>
          <Button disabled={isSaving} onClick={handleSubmit}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProfessionalButton;
